import { ButtonBase, Stack, useMediaQuery, useTheme } from "@mui/material"
import { colors } from "../../services/config/colors"
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import Title from "./Title"
import searchPrimaryIcon from "../../assets/icons/search-primary.svg"
import settingsPrimaryIcon from "../../assets/icons/settings-primary.svg"
import bellPrimaryIcon from "../../assets/icons/bell-primary.svg"
import Button from "./Button"
import exportPrimaryIcon from "../../assets/icons/export-primary.svg"
import Filter from "./Filter"
import { FilterType } from "../../services/config/enum"
import { MainContext } from "../../controllers/main"
import {
  availableLanguages,
  defaultTransition,
  showPageContainerIcons,
} from "../../services/config/constants"
import FilterTag from "./FilterTag"
import { AnimatePresence, motion } from "framer-motion"
import { useNavigate } from "react-router-dom"
import chevronLeftPrimaryIcon from "../../assets/icons/chevron-left-primary.svg"
import Tabs from "./Tabs"
import DateRangePicker from "./DateRangePicker"
import { toCanvas } from "html-to-image"
import { jsPDF } from "jspdf"
import { AuthContext } from "../../controllers/auth"
import slugify from "slugify"
import menuPrimary from "../../assets/icons/menu-primary.png"
import SelectComplex from "./SelectComplex"
import { client } from "../.."
import { CSVLink } from "react-csv"
import { HelperContext } from "../../controllers/helper"
import infoPrimaryIcon from "../../assets/icons/info-primary.svg"
import AlertInfo from "./AlertInfo"
import { useTranslation } from "react-i18next"

const PageContainer = ({
  title,
  breadcrumbs,
  withFilters = true,
  withDatePicker = true,
  withExportButton = true,
  paddingTop = 48,
  withTabs = false,
  tabs,
  selectedTab,
  setSelectedTab,
  tabsDisabled = false,
  children,
  variant = "normal",
  onScroll,
  csvData,
  csvFileName,
  datePickerDisabled,
  infoText,
  backPath,
  withMinHeightZero,
}: {
  title: string | undefined
  breadcrumbs: { title: string; path?: string }[]
  withFilters?: boolean
  withDatePicker?: boolean
  withExportButton?: boolean
  paddingTop?: 0 | 24 | 48
  withTabs?: boolean
  tabs?: string[]
  selectedTab?: number
  setSelectedTab?: Dispatch<SetStateAction<number>>
  tabsDisabled?: boolean
  children?: ReactNode
  variant?: "normal" | "compact"
  onScroll?: () => void
  csvData?: string[][] | (() => Promise<string[][]>)
  csvFileName?: string
  datePickerDisabled?: boolean
  infoText?: string
  backPath?: string
  withMinHeightZero?: boolean
}) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const matchesSM = useMediaQuery(theme.breakpoints.up("sm"))
  const matchesLG = useMediaQuery(theme.breakpoints.up("lg"))
  const navigate = useNavigate()
  const { team, setModalMenuOpen, getUserTeamsList } = useContext(AuthContext)
  const { selectedFilters, setSelectedFilters } = useContext(MainContext)
  const { loading, getData } = useContext(HelperContext)

  // export page
  const screenshotPadding = 30

  const containerRef = useRef<HTMLDivElement>(null)
  const [exporting, setExporting] = useState<boolean>(false)

  const exportPage = useCallback(() => {
    if (containerRef.current === null) {
      return
    }

    setExporting(true)

    toCanvas(containerRef.current, {
      cacheBust: false,
      backgroundColor: colors.background,
      width: containerRef.current.clientWidth + screenshotPadding * 2,
      height: containerRef.current.clientHeight + screenshotPadding * 2,
      style: { padding: `${screenshotPadding}px` },
    })
      .then((canvas) => {
        const doc = new jsPDF({
          orientation: "p",
          unit: "px",
          format: [canvas.width, canvas.height],
          compress: true,
        })
        doc.addImage(
          canvas,
          "PNG",
          0,
          0,
          canvas.width,
          canvas.height,
          "",
          "FAST"
        )

        doc.save(
          `${slugify(team!.title.toLowerCase())}_report_${
            new Date().toISOString().split("T")[0]
          }.pdf`
        )

        setExporting(false)
      })
      .catch((err) => {
        console.log(err)

        setExporting(false)
      })
  }, [containerRef, team])

  // filter remove timeoutId
  const filterTimeoutId = useRef<any>(null)

  // csv download button
  const csvButtonRef = useRef<any>(null)

  // info alert
  const [infoAlertOpen, setInfoAlertOpen] = useState<boolean>(false)

  // active filters to show
  const activeFilters = useMemo(
    () =>
      selectedFilters.filter(
        (filter) => filter.type !== "sdg" && filter.type !== "topic"
      ),
    [selectedFilters]
  )

  // csv export logic
  const [csvDataToExport, setCsvDataToExport] = useState<string[][]>([])

  useEffect(() => {
    if (csvDataToExport.length) {
      csvButtonRef.current.link.click()
    }
  }, [csvDataToExport])

  const exportCsv = async () => {
    setExporting(true)

    const data = typeof csvData === "function" ? await csvData() : csvData
    setCsvDataToExport(data ?? [])

    setExporting(false)
  }

  return (
    <Stack
      id="page-container"
      alignItems="center"
      style={{
        width: "100%",
        height: "100%",
        minHeight: "100%",
        maxHeight: "100%",
        backgroundColor: colors.background,
        padding: matchesSM ? 24 : 12,
        overflowY: "scroll",
        overflowX: "hidden",
      }}
      onScroll={onScroll}
    >
      <Stack
        ref={containerRef}
        style={{
          width: "100%",
          maxWidth: 1200,
          flex: 1,
          ...(withMinHeightZero ? { minHeight: 0 } : {}),
        }}
      >
        {/* breadcrumbs and icons */}
        <div
          style={{
            width: "100%",
            height: 38,
            minHeight: 38,
            position: "relative",
          }}
        >
          {/* breadcrumbs */}
          <Stack direction="row" alignItems="center" gap={1}>
            <ButtonBase disableRipple disabled>
              <Title
                fontSize={14}
                color={colors.textSecondary}
                style={{ letterSpacing: "1px" }}
              >
                {team?.title}
              </Title>
            </ButtonBase>
            {breadcrumbs.map((item, index) => (
              <Stack key={index} direction="row" alignItems="center" gap={1}>
                <Title
                  fontSize={14}
                  color={colors.textSecondary}
                  style={{ letterSpacing: "1px" }}
                >
                  /
                </Title>
                <ButtonBase
                  disableRipple
                  disabled={!item.path}
                  onClick={() => {
                    if (item.path) {
                      navigate(item.path)
                    }
                  }}
                >
                  <Title
                    fontSize={14}
                    color={colors.textSecondary}
                    style={{ letterSpacing: "1px" }}
                    loading={!item.title}
                  >
                    {item.title}
                  </Title>
                </ButtonBase>
              </Stack>
            ))}
          </Stack>
          {/* language select */}
          <SelectComplex
            options={availableLanguages.map((item) => {
              return {
                ...item,
                label: t(item.label),
              }
            })}
            selectedOption={{
              ...availableLanguages.find((item) => item.id === i18n.language)!,
              label: t(
                availableLanguages.find((item) => item.id === i18n.language)!
                  .label
              ),
            }}
            onOptionChange={async (newOption) => {
              if (newOption.id !== i18n.language) {
                // change language in i18n and localStorage
                i18n.changeLanguage(newOption.id)
                localStorage.setItem("language", newOption.id)

                // empty apollo client cache (problem with lang change)
                await client.clearStore()

                // get all data with the new language
                getData()
                getUserTeamsList()
              }
            }}
            style={{
              width: matchesSM ? 160 : 110,
              height: 38,
              position: "absolute",
              top: 0,
              right: 0,
            }}
            popoverWidth={matchesSM ? 160 : 110}
          />
          {/* icons */}
          {variant === "normal" && showPageContainerIcons ? (
            <Stack
              direction="row"
              gap={2}
              style={{ position: "absolute", right: 0, top: 0 }}
            >
              <ButtonBase
                disabled
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  backgroundColor: colors.backgroundWhite,
                }}
              >
                <img
                  src={searchPrimaryIcon}
                  style={{ width: 15, height: 15, opacity: 0.4 }}
                />
              </ButtonBase>
              <ButtonBase
                disabled
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  backgroundColor: colors.backgroundWhite,
                }}
              >
                <img
                  src={settingsPrimaryIcon}
                  style={{ width: 15, height: 15, opacity: 0.4 }}
                />
              </ButtonBase>
              <ButtonBase
                disabled
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  backgroundColor: colors.backgroundWhite,
                }}
              >
                <img
                  src={bellPrimaryIcon}
                  style={{ width: 15, height: 15, opacity: 0.4 }}
                />
              </ButtonBase>
            </Stack>
          ) : null}
        </div>
        {!matchesLG && (
          <Button
            style={{ width: 40, height: 40 }}
            onClick={() => {
              setModalMenuOpen(true)
            }}
            loading={loading}
          >
            <img src={menuPrimary} style={{ width: 16, height: 16 }} alt="" />
          </Button>
        )}
        {/* header */}
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          style={{
            width: "100%",
            height: 40,
            position: "relative",
            marginTop: matchesLG ? 24 : 12,
          }}
        >
          {breadcrumbs.length > 1 && (
            <ButtonBase
              style={{
                width: 32.5,
                minWidth: 32.5,
                height: 32.5,
                borderRadius: "100%",
              }}
              onClick={() => {
                const path = window.location.pathname
                navigate(backPath ?? path.slice(0, path.lastIndexOf("/")))
              }}
            >
              <img src={chevronLeftPrimaryIcon} alt="" />
            </ButtonBase>
          )}
          <AnimatePresence>
            <div
              style={{
                height: 32.5,
                position: "relative",
                flex: 1,
              }}
            >
              <motion.div
                key={title}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{
                  scale: { duration: 0.4, type: "spring" },
                  opacity: { duration: 0.1 },
                }}
                style={{ maxWidth: "100%", position: "absolute" }}
              >
                <Stack gap={1} direction="row">
                  <Title
                    color={colors.primary}
                    fontWeight={900}
                    lineHeight="32.5px"
                    style={{
                      textTransform: "uppercase",
                    }}
                    ellipsis
                    skeletonWidth={200}
                    loading={!title}
                  >
                    {title}
                  </Title>
                  {infoText ? (
                    <ButtonBase
                      title="Info"
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "100%",
                      }}
                      onClick={() => setInfoAlertOpen(true)}
                    >
                      <img
                        src={infoPrimaryIcon}
                        style={{ width: "100%", height: "100%" }}
                      />
                    </ButtonBase>
                  ) : null}
                </Stack>
              </motion.div>
            </div>
          </AnimatePresence>
          <Stack
            direction="row"
            gap={matchesSM ? 2 : 1.5}
            style={{ height: 40, position: "absolute", right: 0 }}
          >
            {withDatePicker && (
              <DateRangePicker
                primary={variant === "normal"}
                disabled={datePickerDisabled}
              />
            )}
            {(variant === "compact" || !matchesSM) && withExportButton ? (
              <>
                <Button
                  style={{ width: 40 }}
                  loading={exporting}
                  onClick={async () => {
                    if (csvData && csvButtonRef.current) {
                      exportCsv()
                    } else {
                      exportPage()
                    }
                  }}
                >
                  <img
                    src={exportPrimaryIcon}
                    style={{ width: 15, height: 15 }}
                    alt=""
                  />
                </Button>
                <CSVLink
                  data={csvDataToExport}
                  ref={csvButtonRef}
                  filename={`${csvFileName ? csvFileName + "_" : ""}${
                    new Date().toISOString().split("T")[0]
                  }.csv`}
                  style={{
                    display: "none",
                  }}
                />
              </>
            ) : null}
            {variant === "normal" && withExportButton && matchesSM ? (
              <Button
                loading={exporting}
                disabled={loading}
                onClick={exportPage}
                style={{ width: 160 }}
              >
                <Stack direction="row" alignItems="center" style={{ gap: 10 }}>
                  <img
                    src={exportPrimaryIcon}
                    style={{ width: 15, height: 15 }}
                    alt=""
                  />
                  <div>{t("export")}</div>
                </Stack>
              </Button>
            ) : null}
          </Stack>
        </Stack>
        {/* tabs */}
        {withTabs &&
        tabs &&
        tabs.length &&
        selectedTab !== undefined &&
        setSelectedTab ? (
          <Tabs
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            variant="slim"
            disabled={tabsDisabled}
            style={{ marginTop: 30 }}
          />
        ) : null}
        {/* filters */}
        {withFilters && (
          <Stack
            direction="row"
            gap={2}
            style={{ width: "100%", height: 74, marginTop: 32 }}
          >
            <Filter
              type={FilterType.location}
              selectedOptions={selectedFilters}
              setSelectedOptions={setSelectedFilters}
            />
            <Filter
              type={FilterType.group}
              selectedOptions={selectedFilters}
              setSelectedOptions={setSelectedFilters}
            />
            {/* <Filter
              type={FilterType.gender}
              selectedOptions={selectedFilters}
              setSelectedOptions={setSelectedFilters}
            />
            <Filter
              type={FilterType.age}
              selectedOptions={selectedFilters}
              setSelectedOptions={setSelectedFilters}
            /> */}
          </Stack>
        )}
        {/* active filters */}
        {withFilters && (
          <Stack
            style={{
              maxHeight: activeFilters.length
                ? activeFilters.length > 1
                  ? 113
                  : 65
                : 0,
              marginTop: activeFilters.length ? 24 : 0,
              opacity: activeFilters.length ? 1 : 0,
              transition: defaultTransition,
              overflow: "hidden",
            }}
          >
            <Stack
              direction="row"
              style={{ width: 320, height: 17, position: "relative" }}
            >
              <Title fontSize={14} lineHeight="17px">
                {t("showing")}
              </Title>
              <ButtonBase
                disabled={loading}
                disableRipple
                style={{ position: "absolute", right: 0, borderRadius: 4 }}
                onClick={() => {
                  selectedFilters.splice(0, selectedFilters.length)
                  setSelectedFilters([...selectedFilters])
                  getData()
                }}
              >
                <Title
                  fontSize={14}
                  lineHeight="17px"
                  color={colors.primary}
                  style={{
                    transition: defaultTransition,
                    opacity: loading ? 0.4 : 1,
                  }}
                >
                  {t("clear_all")}
                </Title>
              </ButtonBase>
            </Stack>
            <Stack
              direction="row"
              gap={2}
              flexWrap="wrap"
              style={{
                width: "100%",
                height: "auto",
                minHeight: 32,
                marginTop: 16,
              }}
            >
              {activeFilters.map((filter) => (
                <FilterTag
                  key={filter.id}
                  name={filter.label}
                  onRemove={() => {
                    if (filterTimeoutId.current) {
                      clearTimeout(filterTimeoutId.current)
                    }

                    const indexToRemove = selectedFilters.findIndex(
                      (item) => item.id === filter.id
                    )
                    selectedFilters.splice(indexToRemove, 1)
                    setSelectedFilters([...selectedFilters])

                    filterTimeoutId.current = setTimeout(getData, 1000)
                  }}
                />
              ))}
            </Stack>
          </Stack>
        )}
        {/* page content */}
        <Stack
          style={{
            paddingTop: paddingTop,
            flex: 1,
            ...(withMinHeightZero ? { minHeight: 0 } : {}),
            position: "relative",
          }}
        >
          {children}
        </Stack>
      </Stack>
      {/* info alert */}
      <AlertInfo
        open={infoAlertOpen}
        setOpen={setInfoAlertOpen}
        title={title ?? ""}
        description={infoText ?? ""}
      />
    </Stack>
  )
}

export default PageContainer
